// src/utils/flowsenseBus.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — tiny cross-page / cross-tab event bus
//
// The backend WebSocket tells every page about server-side changes. This bus
// covers the gap in between: when THIS browser changes something (a work order
// is completed, an asset is reported failed), other pages in the same tab and
// other open tabs refresh immediately instead of waiting for the next poll or
// for the server to broadcast.
//
//   publish('work_order_changed', { id, action: 'status', status: 'completed', asset_id })
//   const off = subscribe((type, payload) => …)
//
// Uses BroadcastChannel when available (other tabs) + a window CustomEvent
// (same tab). Messages are tiny and fire-and-forget.
// ─────────────────────────────────────────────────────────────────────────────
const CHANNEL = 'flowsense';
const WINDOW_EVENT = 'flowsense:bus';

let channel = null;
const getChannel = () => {
    if (channel || typeof window === 'undefined' || typeof window.BroadcastChannel !== 'function') return channel;
    try { channel = new window.BroadcastChannel(CHANNEL); } catch { channel = null; }
    return channel;
};

export const BUS_EVENTS = Object.freeze({
    WORK_ORDER_CHANGED: 'work_order_changed',
    ASSET_CHANGED: 'asset_changed',
    ROAD_CHANGED: 'road_changed',
});

export function publish(type, payload = {}) {
    const msg = { type, payload, at: Date.now(), source: 'local' };
    try { window.dispatchEvent(new window.CustomEvent(WINDOW_EVENT, { detail: msg })); } catch { /* SSR / old browsers */ }
    try { getChannel()?.postMessage({ ...msg, source: 'tab' }); } catch { /* ignore */ }
}

/** @returns {() => void} unsubscribe */
export function subscribe(handler) {
    if (typeof window === 'undefined') return () => {};
    const onWindow = (e) => { const m = e.detail; if (m) handler(m.type, m.payload, m); };
    const onChannel = (e) => { const m = e.data; if (m && m.type) handler(m.type, m.payload, m); };
    window.addEventListener(WINDOW_EVENT, onWindow);
    const ch = getChannel();
    ch?.addEventListener?.('message', onChannel);
    return () => {
        window.removeEventListener(WINDOW_EVENT, onWindow);
        ch?.removeEventListener?.('message', onChannel);
    };
}

export default { publish, subscribe, BUS_EVENTS };